import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions } from '../../redux/slices/cartSlice';
import { wishlistActions } from '../../redux/slices/wishlistSlice';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuth from '../../custom-hooks/useAuth';
import { getProductImg, handleImgError } from '../../utils/imageHelper';

const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const dispatch = useDispatch();
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!product) return null;

  const isWishlisted = wishlistItems.some((item) => item.id === product.id);

  const handleAddToCart = () => {
    if (!currentUser) {
      toast.info('Please sign in to add items to your cart & sync your profile');
      onClose();
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    for (let i = 0; i < quantity; i++) {
      dispatch(
        cartActions.addItem({
          id: product.id,
          productName: product.productName,
          price: product.price,
          imgUrl: product.imgUrl,
        })
      );
    }
    toast.success(`Added ${quantity} item${quantity > 1 ? 's' : ''} to bag!`);
    onClose();
  };

  const handleToggleWishlist = () => {
    if (!currentUser) {
      toast.info('Please sign in to save pieces to your wishlist');
      onClose();
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    dispatch(wishlistActions.toggleWishlist(product));
    if (isWishlisted) {
      toast.info('Removed from wishlist');
    } else {
      toast.success('Saved to wishlist!');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="quickview__backdrop" onClick={onClose}>
          <motion.div
            className="quickview__modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              className="quickview__close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              <i className="ri-close-line"></i>
            </button>

            <div className="quickview__grid">
              <div className="quickview__img-side">
                <img
                  src={getProductImg(product)}
                  onError={(e) => handleImgError(e, product)}
                  alt={product.productName}
                />
              </div>

              <div className="quickview__info-side">
                <span className="quickview__category">{product.category}</span>
                <h3 className="quickview__title">{product.productName}</h3>

                <div className="quickview__rating">
                  <div className="quickview__stars">
                    <i className="ri-star-s-fill"></i>
                    <i className="ri-star-s-fill"></i>
                    <i className="ri-star-s-fill"></i>
                    <i className="ri-star-s-fill"></i>
                    <i className="ri-star-half-s-fill"></i>
                  </div>
                  <span>({product.avgRating || 4.5} rating)</span>
                </div>

                <div className="quickview__price">${product.price}</div>

                <p className="quickview__desc">
                  {product.shortDesc ||
                    'Handcrafted contemporary luxury furniture piece designed for modern interiors.'}
                </p>

                <div className="quickview__actions">
                  <div className="quickview__qty-box">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span>{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => q + 1)}
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  <button
                    type="button"
                    className="buy__btn flex-grow-1"
                    onClick={handleAddToCart}
                  >
                    <i className="ri-shopping-bag-line me-1"></i> Add to Bag (${product.price * quantity})
                  </button>

                  <button
                    type="button"
                    className={`quickview__wishlist-btn ${isWishlisted ? 'active' : ''}`}
                    onClick={handleToggleWishlist}
                    title={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
                  >
                    <i className={isWishlisted ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  </button>
                </div>

                <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                  <span className="text-success small fw-semibold">
                    <i className="ri-checkbox-circle-line me-1"></i> In Stock & Ready to Dispatch
                  </span>
                  <Link
                    to={`/shop/${product.id}`}
                    className="small fw-semibold text-primary text-decoration-underline"
                    onClick={onClose}
                  >
                    View Full Specifications &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QuickViewModal;
